import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  Workflow,
  Users,
  BookOpen,
  Network,
  Activity,
  GitCompare,
  Cloud,
  Compass,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const TITLE =
  'Oracle BPM Suite Migration — 12c to 14c Workflow Modernization | EZ Cloud';
const DESCRIPTION =
  'Oracle BPM Suite 12c (12.2.1.4) is part of the Fusion Middleware 12c support timeline ending December 2026. BPM Suite drives AP approval workflow, Human Task routing, and Business Rules for many WebCenter AP customers. The forward path is the upgrade to BPM Suite 14c via the SOA Suite + BPM combined upgrade. EZ Cloud delivers BPM composite migration, workflow modernization, and AME routing review.';
const CANONICAL = 'https://ezcloud.ai/solutions/oracle-bpm-suite-migration';

const KEY_FACTS = [
  {
    icon: Workflow,
    title: 'BPMN process modeling',
    detail:
      'BPM Suite hosts the BPMN 2.0 process models that orchestrate AP approval workflow — invoice routing, exception branches, escalation timers, parallel approver paths, and conditional logic on coding fields.',
  },
  {
    icon: Users,
    title: 'Human Task routing',
    detail:
      'Human Workflow (Human Task service) handles approver assignment, claim/release, delegation, and task notification. For AP, this is the layer that decides who sees an invoice next and what they can do with it.',
  },
  {
    icon: BookOpen,
    title: 'Business Rules dictionaries',
    detail:
      'Oracle Business Rules dictionaries hold the decision tables, rulesets, and bucketsets that drive approval thresholds, GL coding defaults, and routing decisions. Many AP customers have heavily customized these over years of fine-tuning.',
  },
  {
    icon: Network,
    title: 'Integrated with SOA Suite composites',
    detail:
      'BPM Suite runs inside the same SOA infrastructure domain as SOA Suite composites. BPMN processes invoke BPEL services, Mediator routing, and adapters — and SOA composites call into BPM Human Tasks. The two products are operationally one stack.',
  },
  {
    icon: Activity,
    title: 'Oracle Business Activity Monitoring (BAM)',
    detail:
      'BAM provides dashboards and KPIs over the BPM and SOA runtime — invoice aging, approval cycle times, SLA breaches, and exception rates. For AP teams running BAM as their workflow-visibility layer, BAM lifecycle moves with BPM and SOA.',
  },
];

const FORWARD_PATH = [
  {
    step: '1',
    title: 'Inventory the BPM Suite footprint',
    body: 'Catalogue every BPMN composite, Human Task definition, Business Rules dictionary, BAM data object, and the SOA adapters / BPEL services they invoke. Surface customizations to task forms (ADF Task Flows), notification templates, and any external integrations (EBS AME, Fusion approvals, identity provider).',
  },
  {
    step: '2',
    title: 'Combined SOA Suite + BPM Suite upgrade',
    body: 'BPM Suite 12c and SOA Suite 12c share an infrastructure domain. The 12c → 14c forward path is the combined SOA Suite + BPM Suite upgrade using Oracle\'s Upgrade Assistant — same domain, same upgrade utility, same out-of-place pattern as the SOA Suite migration. They cannot be sensibly upgraded independently.',
  },
  {
    step: '3',
    title: 'Migrate composites, Human Tasks, and Business Rules',
    body: 'BPMN composites and Human Task definitions migrate forward into the new 14c domain. Business Rules dictionaries transfer with validation — the rule engine is compatible but custom Java-backed functions and any RL (Rules Language) usage need review. ADF task forms need ADF 14c compatibility review.',
  },
  {
    step: '4',
    title: 'Validate AME and external integrations',
    body: 'EBS AME tables and BPM Suite approval workflow are tightly coupled in many AP implementations — AME may decide the approver list and hand it to BPM, or BPM may call AME for routing. Both paths need re-validation against 14c. Fusion approvals (Approval Management Service) integration patterns are similarly re-tested.',
  },
  {
    step: '5',
    title: 'BAM migration and dashboard rebuild',
    body: 'BAM 14c carries forward most 12c data objects and dashboards, but custom enterprise message sources and external data feeds need validation. For AP teams running heavy BAM dashboards over invoice aging and approval cycle time, this is a non-trivial sub-stream of the project.',
  },
  {
    step: '6',
    title: 'Parallel UAT and cutover',
    body: 'Run 12c and 14c BPM domains in parallel during UAT. Validate every approval path, every Business Rules outcome, every Human Task assignment, every BAM KPI. Cut over only after end-to-end testing against real AP invoice samples — including the exception branches that rarely fire but matter when they do.',
  },
];

const SCENARIOS = [
  {
    title: 'Heavy customization in Business Rules and BPMN that nobody can read',
    body:
      'Business Rules dictionaries built up over a decade — decision tables with hundreds of rows, custom RL functions, bucketsets that map to GL segments. BPMN processes with dozens of gateways and exception sub-processes. The original implementation team is gone; what remains is a workflow that runs and a team that cannot explain why a given invoice took the path it did.',
  },
  {
    title: 'AME inside EBS coordinating with BPM Suite for approvals',
    body:
      'A pattern we see often: EBS AME owns the approval-chain calculation against HR hierarchy and coding, then hands the chain to BPM Suite for actual task routing, notifications, and SLA tracking. The forward path needs to preserve that contract — AME stays where it is, BPM moves to 14c, and the integration between them is re-tested as a first-class deliverable.',
  },
  {
    title: 'Custom Human Task workflow for AP that needs to migrate forward',
    body:
      'Custom ADF Task Flows over Human Tasks, custom notification templates, custom escalation timers, custom delegation rules. ADF customizations need ADF 14c compatibility review. Notification templates need to be re-pointed at 14c\'s notification framework. Escalation timers need to be re-validated against the 14c scheduler.',
  },
  {
    title: 'Migration to Oracle Integration Cloud (OIC) as an alternative',
    body:
      'Some customers use the 14c decision window to evaluate whether BPM Suite still fits, or whether Oracle Integration Cloud (OIC) with its Process automation and structured-process capabilities is the better forward target. OIC is not a drop-in replacement for BPM Suite — but for AP workflow that has simplified over time, it can be a legitimate alternative path.',
  },
];

const BPM_VS_OIC = [
  {
    dimension: 'Deployment model',
    bpm14c: 'On-premise or OCI Marketplace VM, customer-managed domain',
    oic: 'Oracle-managed SaaS, no infrastructure to run',
  },
  {
    dimension: 'BPMN process modeling',
    bpm14c: 'Full BPMN 2.0, deeply customizable, long-running processes, complex compensation',
    oic: 'Structured Processes (lighter BPMN), Dynamic Processes (case-management style)',
  },
  {
    dimension: 'Business Rules',
    bpm14c: 'Oracle Business Rules dictionaries (carry forward from 12c)',
    oic: 'Decisions service — simpler, no direct migration from Oracle Business Rules',
  },
  {
    dimension: 'Existing customization carry-forward',
    bpm14c: 'High — BPMN, Human Tasks, Business Rules, ADF Task Flows migrate forward',
    oic: 'Low — most existing assets need to be redesigned, not migrated',
  },
  {
    dimension: 'AP approval workflow fit',
    bpm14c: 'Fits the heavy-customization, long-running, exception-rich AP pattern',
    oic: 'Fits AP teams that have simplified to fewer branches and want SaaS workflow',
  },
  {
    dimension: 'Effort to get to a supported state',
    bpm14c: 'Combined SOA + BPM upgrade project, validation-heavy',
    oic: 'Redesign + rebuild of the workflow layer, integration-heavy',
  },
];

const FAQS = [
  {
    q: 'Is Oracle BPM Suite being killed?',
    a: 'No. BPM Suite 14c is the forward path and carries Premier Support runway through approximately December 2030 and Extended Support through December 2033, in line with the rest of the Fusion Middleware 14c family. Oracle BPM Suite 12c is part of the Fusion Middleware 12c support cliff in December 2026, but customers who depend on BPM Suite for AP approval workflow have a supported upgrade target — it is not a discontinued product.',
  },
  {
    q: 'What is the upgrade pattern for BPM Suite 12c to 14c?',
    a: 'BPM Suite 12c to 14c is a combined SOA Suite + BPM Suite upgrade using Oracle\'s Upgrade Assistant, because the two products share an infrastructure domain. Oracle\'s recommended approach is the out-of-place domain directory upgrade: a new 14c domain is created, the 12c configuration is migrated forward, BPMN composites and Human Task definitions are re-deployed, and Business Rules dictionaries are validated. BPM Suite and SOA Suite cannot be sensibly upgraded independently of each other.',
  },
  {
    q: 'Is BPM Suite 14c a long-term-support release?',
    a: 'Yes — Oracle Fusion Middleware 14c (14.1.2), which includes BPM Suite, is positioned as the long-runway release for the Fusion Middleware family. Premier Support runs through approximately December 2030 and Extended Support through December 2033 under Oracle\'s Lifetime Support Policy. For BPM Suite customers, the 14c upgrade buys approximately 8 years of supported runway from the 2026 cliff before the same lifecycle decision recurs.',
  },
  {
    q: 'What does the OIC migration path look like as an alternative?',
    a: 'Migration from BPM Suite to Oracle Integration Cloud (OIC) is a redesign rather than an upgrade, because BPMN composites, Business Rules dictionaries, and ADF Task Flows do not transfer directly. OIC is a managed SaaS offering that includes Process automation — Structured Processes (lighter BPMN) and Dynamic Processes (case-management style) — plus a Decisions service for rules. For AP teams that have simplified their workflow over time and want to exit the customer-managed-domain model, OIC is a legitimate alternative; for heavy-customization AP teams, BPM Suite 14c usually fits better.',
  },
  {
    q: 'How do Business Rules dictionaries migrate to 14c?',
    a: 'Business Rules dictionaries migrate as part of the Upgrade Assistant pass, and the Oracle Business Rules engine carries forward into 14c. The validation work concentrates on custom Java functions invoked from rules, any direct RL (Rules Language) usage, dictionary references to external Java classes, and any bucketsets sourced from external data. Decision tables and rulesets composed of standard rule operators typically transfer with no functional change.',
  },
  {
    q: 'How does Human Task workflow migrate from 12c to 14c?',
    a: 'Human Task definitions, assignment rules, escalation timers, and notification templates carry forward through the Upgrade Assistant. The validation work concentrates on ADF Task Flows (which need ADF 14c compatibility review), any custom notification headers or external mail integrations, and identity-provider configuration (LDAP, SAML, or whichever provider is wired to the BPM identity service). Custom claim/release logic and delegation rules transfer but should be regression-tested.',
  },
  {
    q: 'How does BPM Suite integrate with WebCenter AP workflow?',
    a: 'BPM Suite is the workflow layer in the common WebCenter AP architecture — it owns the BPMN approval process, the Human Task routing, and the Business Rules that decide thresholds and paths. WebCenter Imaging or WebCenter Content holds the invoice document and metadata, Forms Recognition extracts the data, and SOA Suite composites move the invoice payload to the ERP and the workflow layer. A 14c upgrade of the WebCenter and SOA layers without also upgrading BPM Suite leaves the workflow brain on 12c, which is why these are typically scoped together.',
  },
  {
    q: 'What is a typical timeline for a BPM Suite 12c to 14c upgrade?',
    a: 'Combined SOA Suite + BPM Suite 14c upgrades typically run 6-12 months end to end. Timeline depends on the depth of Business Rules customization, the number and complexity of BPMN composites, the breadth of BAM dashboards, and the maturity of the existing AME / Fusion approvals integration. Inventory and assessment typically takes 3-5 weeks, and the validation phase tends to be the longest sub-stream because every approval branch and every Business Rules outcome needs to be exercised against real AP invoice samples.',
  },
];

const OracleBpmSuiteMigration = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: TITLE,
    description: DESCRIPTION,
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': CANONICAL,
    },
    about: [
      { '@type': 'Thing', name: 'Oracle BPM Suite' },
      { '@type': 'Thing', name: 'Oracle BPM Suite 12c' },
      { '@type': 'Thing', name: 'Oracle BPM Suite 14c' },
      { '@type': 'Thing', name: 'BPMN' },
      { '@type': 'Thing', name: 'Oracle Human Workflow' },
      { '@type': 'Thing', name: 'Oracle Business Rules' },
      { '@type': 'Thing', name: 'Oracle Integration Cloud' },
    ],
  };

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to migrate Oracle BPM Suite from 12c to 14c',
    description: 'The combined Oracle SOA Suite and BPM Suite 12c to 14c upgrade pattern. Out-of-place domain directory upgrade and composite migration.',
    totalTime: 'P9M',
    step: FORWARD_PATH.map((s, idx) => ({
      '@type': 'HowToStep',
      position: idx + 1,
      name: s.title,
      text: s.body,
      url: 'https://ezcloud.ai/solutions/oracle-bpm-suite-migration#step-' + (idx + 1),
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={TITLE}
        description={DESCRIPTION}
        canonical={CANONICAL}
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'Oracle BPM Suite Migration',
            url: CANONICAL,
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Workflow className="w-4 h-4" aria-hidden />
              Oracle BPM Suite · 12c → 14c · Workflow modernization
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Oracle BPM Suite migration
              <span className="block text-accent mt-3 text-2xl md:text-3xl lg:text-4xl font-bold">
                Combined SOA Suite + BPM Suite 14c upgrade. Approval workflow modernization. AME and Human Task routing review.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              Oracle BPM Suite 12c is part of the December 2026 Fusion Middleware support cliff.
              The forward path runs through the combined SOA Suite + BPM Suite 14c upgrade.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('bpm-suite-migration-hero')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a BPM Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What this is
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              Oracle BPM Suite migration, defined
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Oracle BPM Suite 12c (12.2.1.4) sits on the same Fusion Middleware support
                  timeline as SOA Suite and WebCenter — Premier Support exits in December 2026.
                </strong>{' '}
                For most WebCenter AP implementations, BPM Suite is the layer that drives invoice
                approval workflow, Human Task routing, Business Rules thresholds, and the BAM
                dashboards finance leaders watch.
              </p>
              <p>
                The forward path is the{' '}
                <strong className="text-gray-900">
                  combined SOA Suite + BPM Suite 14c upgrade
                </strong>
                . The two products share an infrastructure domain and the same Upgrade Assistant
                — they are upgraded together, out-of-place, into a new 14c domain. BPMN
                composites, Human Task definitions, Business Rules dictionaries, and ADF Task
                Flows migrate forward with validation. The integration with EBS AME and Fusion
                approvals is re-tested against 14c as a first-class deliverable.
              </p>
              <p>
                The same lifecycle window is the right moment to compare BPM Suite 14c against
                Oracle Integration Cloud (OIC) Process automation as an alternative — not because
                OIC is a drop-in replacement, but because some AP teams have simplified their
                workflow enough that OIC becomes a legitimate alternative target. Heavy-customization
                AP workflow usually fits BPM Suite 14c better; simpler workflow can fit OIC.
              </p>
            </div>
          </div>
        </section>

        {/* What BPM Suite is in the WebCenter AP context */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What BPM Suite is in the WebCenter AP context
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Five things BPM Suite does for AP teams running WebCenter
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              BPM Suite is rarely deployed in isolation. In a WebCenter AP architecture it is the
              workflow brain — the layer that decides who approves what, against which rules, and
              what happens when an invoice goes off the happy path.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {KEY_FACTS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* The forward path */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The forward path: BPM 12c → 14c
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              The six steps of a combined SOA + BPM Suite 14c upgrade
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              BPM Suite 12c and SOA Suite 12c share an infrastructure domain. The forward path is
              the combined upgrade, out-of-place, into a new 14c domain — the same upgrade
              utility, the same domain pattern, validated against a 14c compatibility matrix.
            </p>
            <div className="space-y-5">
              {FORWARD_PATH.map((s) => (
                <Card key={s.step} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-lg bg-accent/15 flex items-center justify-center text-accent font-bold text-xl flex-shrink-0">
                      {s.step}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{s.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/solutions/oracle-soa-suite-migration"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                Read the SOA Suite migration companion page
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* Common BPM scenarios */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common BPM scenarios we hear
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Where BPM Suite customers are when the 14c question comes up
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Four scenarios that show up repeatedly in BPM Suite discovery calls. None of these
              are blockers — they are the work that any 14c upgrade plan needs to surface and
              cost up front.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {SCENARIOS.map((s) => (
                <Card key={s.title} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 leading-snug">
                    {s.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed text-sm">{s.body}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* BPM Suite vs OIC */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              BPM Suite 14c vs Oracle Integration Cloud
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              When OIC is the right modernization path, and when BPM 14c is the right answer
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Both are legitimate forward targets from the 12c cliff. The right answer turns on
              how much customization needs to carry forward and whether the AP team wants to
              keep running a customer-managed workflow domain.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-200 rounded-xl overflow-hidden bg-white">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-1/5">
                      Dimension
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-2/5">
                      BPM Suite 14c
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-2/5">
                      Oracle Integration Cloud (OIC)
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {BPM_VS_OIC.map((row) => (
                    <tr key={row.dimension} className="border-b border-gray-200 last:border-b-0">
                      <td className="px-5 py-4 font-semibold text-gray-900 bg-gray-50">
                        {row.dimension}
                      </td>
                      <td className="px-5 py-4 text-gray-700">{row.bpm14c}</td>
                      <td className="px-5 py-4 text-gray-700">{row.oic}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-center mt-8">
              <Link
                to="/solutions/webcenter-12c-end-of-support-decision"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See how this fits the full WebCenter 12c end-of-support decision
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* Engagement tiers */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud engages on BPM Suite migrations
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four engagement tiers for the BPM Suite 14c forward path
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The same 25-year founder-level Oracle expertise — including direct experience with
              BPM, SOA Suite, and Human Workflow — applies to BPM Suite 12c → 14c engagements.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Tier 1 · BPM Migration Assessment
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Fixed-fee BPM + SOA inventory and forward plan
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Andrew reads your existing BPM Suite and SOA Suite implementation directly and
                  delivers a combined 14c upgrade plan. Surfaces every BPMN composite, Human Task
                  definition, Business Rules dictionary, AME integration point, and BAM dashboard
                  that needs to migrate forward. Plan is yours to keep regardless of which
                  delivery partner you choose.
                </p>
              </Card>

              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Tier 2 · Approval Workflow Review
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Deeper read of your AP approval workflow
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  A one-week fixed-fee read of your running BPMN processes, Business Rules
                  dictionaries, Human Task assignments, AME integration, and BAM KPIs. Output is a
                  structured workflow inventory used as input to any 14c upgrade plan or OIC
                  evaluation. Useful before scoping any heavier engagement.
                </p>
              </Card>

              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Tier 3 · Strategic Advisory Retainer
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Founder-level guidance through the 14c decision window
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Monthly retainer with Andrew, sized for the customer working through a 6-12 month
                  BPM + SOA upgrade decision with internal stakeholders, procurement, and Oracle.
                  Includes BPM 14c vs OIC comparison work, AME-integration sequencing, and
                  reading vendor proposals against the customer's actual workflow.
                </p>
              </Card>

              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Tier 4 · Forward Deployed Engineering
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Embedded delivery on specific BPM migration outcomes
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Project-based, founder-led delivery on scoped outcomes: combined SOA + BPM
                  domain upgrade, Business Rules dictionary migration audit, Human Task and ADF
                  Task Flow 14c compatibility work, AME / Fusion approvals integration
                  re-validation, BAM dashboard rebuild, or a targeted OIC-feasibility prototype
                  for a single workflow.
                </p>
              </Card>
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Engagement pricing is established per situation on a Decision Call.
            </p>
          </div>
        </section>

        {/* Cross-link to decision guide */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the four forward paths from the 12c cliff — including the
              combined SOA + BPM Suite 14c upgrade — with stack-specific considerations, eight
              diagnostic questions, and a vendor evaluation checklist.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on Oracle BPM Suite migration
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Talk through your BPM Suite migration
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud. 25 years across
              the Oracle ecosystem with direct experience on BPM, SOA Suite, Human Workflow, and
              the WebCenter AP stack BPM Suite typically sits inside. Walk through your BPMN
              composites, Business Rules dictionaries, AME integration, and the combined SOA +
              BPM upgrade path in the context of your specific implementation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('bpm-suite-migration-cta')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a BPM Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default OracleBpmSuiteMigration;
